import { Chip } from '@hologram-dimension/chip';
import type { IconNames } from '@hologram-dimension/icons';
import { useSimStatuses } from 'api';
import { ApiBaseError } from 'api/utils/shared';
import { useMemo } from 'react';
import { EnumsSimNetworkState, EnumsSimStatus } from 'types/__generated__/SimInventoryApi';

export interface SimStatusInfo {
  tooltip: string;
  status: EnumsSimStatus;
  icon: IconNames;
  colorVariant: GetReactParameterBag<typeof Chip>['colorVariant'];
}

interface UseStatusMapReturn {
  statusInfo: SimStatusInfo;
  isLoading: boolean;
  error?: ApiBaseError;
}

type SharedSimStatusInfo = Omit<SimStatusInfo, 'status'>;

const enteringTesting: SharedSimStatusInfo = {
  tooltip:
    'This SIM is being configured for pre-deployment testing. Configuration can take up to 15 minutes. Timing varies based on network partner activity.',
  icon: 'StatusTesting',
  colorVariant: 'yellow',
};

const inPredeploymentTesting: SharedSimStatusInfo = {
  tooltip:
    'This SIM is in pre-deployment testing. The SIM will auto-activate when your testing period ends, it uses 100 KB of data, or sends 10 SMS messages.',
  icon: 'StatusTesting',
  colorVariant: 'purple',
};

const ready: SharedSimStatusInfo = {
  tooltip: 'This SIM is activated and able to connect to the Hologram network.',
  icon: 'StatusReady',
  colorVariant: 'teal',
};

const pending: SharedSimStatusInfo = {
  tooltip: 'This SIM is being configured for use on the Hologram network.',
  icon: 'StatusActivating',
  colorVariant: 'yellow',
};

const useStatusMap = (networkState?: string): UseStatusMapReturn => {
  const { formatStatus, isLoading, error } = useSimStatuses();

  const simStatusTooltipMap = useMemo(() => {
    if (isLoading || error) {
      return new Map<EnumsSimNetworkState | 'PENDING', SimStatusInfo>();
    }

    return new Map<EnumsSimNetworkState | 'PENDING', SimStatusInfo>(
      Object.entries({
        CONNECTED: {
          tooltip:
            'This SIM is currently connected to our network, or has connected recently to our network and has an open data session. Real-time session info varies by carrier.',
          status: 'Connected',
          icon: 'StatusConnected',
          colorVariant: 'lime',
        },
        'TEST-CONNECTED': {
          tooltip:
            'This SIM is currently connected to our network, or has connected recently to our network and has an open data session. Real-time session info varies by carrier. The SIM will auto-activate when your testing period ends, it uses 100 KB of data, or sends 10 SMS messages.',
          status: 'Connected',
          icon: 'StatusConnected',
          colorVariant: 'lime',
        },
        INACTIVE: {
          tooltip:
            'This SIM is assigned to your organization, but has not been activated. Activate it at any time to start using data on the Hologram network.',
          status: formatStatus('INACTIVE'),
          icon: 'StatusReady',
          colorVariant: 'default',
        },
        PENDING: {
          ...pending,
          status: formatStatus('PENDING'),
        },
        'TEST-AUTOACTIVATE': {
          ...inPredeploymentTesting,
          status: formatStatus('TEST-AUTOACTIVATE'),
        },
        'TEST-ACTIVATE': {
          ...inPredeploymentTesting,
          status: formatStatus('TEST-ACTIVATE'),
        },
        'TEST-HIBERNATE': {
          tooltip:
            'This SIM is in pre-deployment testing. This SIM will will hibernate until you’re ready to deploy and choose to activate after the testing period ends, it uses 100 KB of data, or sends 10 SMS messages.',
          status: formatStatus('TEST-HIBERNATE'),
          icon: 'StatusTesting',
          colorVariant: 'purple',
        },
        'TEST-ACTIVATE-PENDING': {
          ...enteringTesting,
          status: formatStatus('TEST-ACTIVATE-PENDING'),
        },
        'TEST-HIBERNATE-PENDING': {
          ...enteringTesting,
          status: formatStatus('TEST-HIBERNATE-PENDING'),
        },
        'TEST-AUTOACTIVATE-PENDING': {
          ...enteringTesting,
          status: formatStatus('TEST-AUTOACTIVATE-PENDING'),
        },
        'INACTIVE-TESTED': {
          tooltip:
            'This SIM has used all available test data. Activate it at any time to start using data on the Hologram network.',
          status: formatStatus('INACTIVE-TESTED'),
          icon: 'StatusTesting',
          colorVariant: 'default',
        },
        'LIVE-PENDING': {
          ...pending,
          status: formatStatus('LIVE-PENDING'),
        },
        LIVE: {
          ...ready,
          status: formatStatus('LIVE'),
        },
        FACTORY: {
          ...ready,
          status: formatStatus('FACTORY'),
        },
        'PAUSED-PENDING-USER': {
          tooltip:
            'This SIM is being paused by a member of your organization. Once paused, it will not be able to transmit data or SMS services.',
          status: formatStatus('PAUSED-PENDING-USER'),
          icon: 'Pause',
          colorVariant: 'lightpink',
        },
        'PAUSED-PENDING-SYS': {
          tooltip:
            'This SIM is being paused due to an insufficient account balance, data usage limit, SMS usage, or another reason. Once paused, it will not be able to transmit data or SMS services.',
          status: formatStatus('PAUSED-PENDING-SYS'),
          icon: 'Pause',
          colorVariant: 'lightpink',
        },
        'PAUSED-USER': {
          tooltip:
            'This SIM has been manually paused by a member of your organization. While paused, your device will not be able to send or receive data or SMS messages.',
          status: formatStatus('PAUSED-USER'),
          icon: 'Pause',
          colorVariant: 'pink',
        },
        'PAUSED-SYS': {
          tooltip:
            'This SIM has been paused and cannot use data or SMS services. Once resolved, it may take up to 10 minutes to resume activity on the Hologram network. SIMs are often paused by reaching a data usage or SMS limit, or a lack of account balance.',
          status: formatStatus('PAUSED-SYS'),
          icon: 'Pause',
          colorVariant: 'pink',
        },
        'DEAD-PENDING': {
          tooltip:
            'This SIM is being permanently deactivated. Once deactivated, it will no longer be able to transmit data or SMS services.',
          status: formatStatus('DEAD-PENDING'),
          icon: 'StatusDeactivated',
          colorVariant: 'black',
        },
        DEAD: {
          tooltip:
            'This device has been permanently deactivated from use on the Hologram network and cannot be reactivated.',
          status: formatStatus('DEAD'),
          icon: 'StatusDeactivated',
          colorVariant: 'black',
        },
      }) as any // TODO: There's probably a way to type this so we can cleanly see missing errors. It eludes me at the moment
    );
  }, [error, formatStatus, isLoading]);

  const statusInfo = useMemo(() => {
    let newStatusInfo = simStatusTooltipMap.get((networkState as any) ?? 'PENDING');

    if (!(isLoading || error) && !newStatusInfo) {
      // We know in this case networkState is *something* becuase we coalesce to a value we know returns a value
      // eslint-disable-next-line no-console
      console.error(`Got an unhandled status: ${networkState}`);
      newStatusInfo = simStatusTooltipMap.get('PENDING');
    }

    return newStatusInfo;
  }, [error, isLoading, networkState, simStatusTooltipMap]);

  return { statusInfo: statusInfo!, isLoading, error };
};

export default useStatusMap;
